import type { AxiosRequestConfig } from '@ahm/api-wrappers-core';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { NearestBranch } from './ahafood';
import { apiRoutes } from './api-routes';
import type { Branch } from './use-get-store-order';
import { webviewHttpClient } from '@/app/[locale]/(order)/components/auth/webview-http-client';
import { createDealQueryKey } from '@/lib/query-key-utils';
import { CACHE_STRATEGIES } from '@/lib/react-query/query-client';

export interface ProgressDeal {
  progress: Progress[];
}

export interface Progress {
  item_id: number;
  item_name: string;
  store_id: number;
  store_name: string;
  store_open_hours: OpenHoursType;
  promo_code: string;
  promo_name: string;
  promo_desc: string;
  discount_config: DiscountConfig;
  max_use: number;
  usage_count: number;
  status: string;
  city_id: string;
  item_image_url: string;
  store_image_url: string;
  store_slug: string;
  page_id: number;
  ai_tags: string[];
  branches: Branch[];
  total_items: number;
  price: number;
  discounted_price: number;
  nearest_branch?: NearestBranch;
  distance?: number;
  is_available: boolean;
  is_scheme: boolean;
  is_ai_image?: boolean;
}

type OpenHoursType = Record<string, { start_time: number; end_time: number }[]>;

export interface TimeSlot {
  start_time: number;
  end_time: number;
}

export interface DiscountConfig {
  type: string;
  value: number;
  max_discount_value: number;
  max_apply_per_item: number;
}

export interface DealProgressParams {
  city_id: string;
  skip?: number;
  limit?: number;
  reach_max_use?: boolean;
  lat?: number;
  lng?: number;
  require_scheme?: boolean;
}

export const getDealProgressFn = async (
  params: DealProgressParams,
  config: AxiosRequestConfig = {}
): Promise<ProgressDeal> => {
  const response = await webviewHttpClient.get<ProgressDeal>(
    apiRoutes.ahafood.web_view.get_deal_progress,
    {
      ...config,
      params: {
        city_id: params.city_id,
        skip: params.skip ?? 0,
        limit: params.limit ?? 20,
        reach_max_use: params.reach_max_use ?? false,
        ...(params.require_scheme !== undefined && {
          require_scheme: params.require_scheme,
        }),
        ...(params.lat &&
          params.lng && {
            lat: params.lat,
            lng: params.lng,
          }),
      },
    }
  );

  return response?.data;
};

export const useGetDealProgress = (
  params: DealProgressParams,
  opts?: Omit<UseQueryOptions<ProgressDeal, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<ProgressDeal, Error>({
    queryKey: createDealQueryKey(params), // Normalized geohash key (~4.9km precision)
    queryFn: () => getDealProgressFn(params), // Still pass exact lat/lng to API
    ...CACHE_STRATEGIES.realtime, // 1min cache - deal progress changes frequently
    ...opts, // opts can override cache strategy if needed
  });
};
